'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LocaleLanguage } from '@/lib/locale-context';
import type { Consultation } from '../types';
import { useConsultationFlow } from './useConsultationFlow';
import { useMultistepNavigation } from './useMultistepNavigation';

export const toggleStringInArray = (arr: string[], val: string) =>
  arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

type Topic = NonNullable<Consultation['topics']>[number];

type UseMultistepFormStateArgs = {
  presetConsultation?: Consultation | null;
  presetTopics?: string | null;
  locale?: LocaleLanguage;
};

export type UseMultistepFormStateResult = {
  totalSteps: number;
  consultations: Consultation[];
  loadingConsultations: boolean;
  selectedConsultation: Consultation | null;
  topics: Topic[];
  selectedConsultationTitle: string;
  selectedTopics: string[];
  anliegen: string;
  currentStep: number;
  canGoBack: boolean;
  canGoNext: boolean;
  showFooter: boolean;
  stepIndicatorCurrent: number;
  isSuccess: boolean;
  isError: boolean;
  isFinalStepSubmitting: boolean;
  presetCallbackRequestNotes: string;
  presetWebsiteSelectedTopics: string;
  registerFinalSubmit: (submit: () => void) => void;
  setSelectedConsultationTitle: (v: string) => void;
  setSelectedTopics: React.Dispatch<React.SetStateAction<string[]>>;
  setAnliegen: (v: string) => void;
  onToggleTopic: (val: string) => void;
  resetAll: () => void;
  onNext: () => void;
  onBack: () => void;
  onSubmitStart: () => void;
  onSubmitSuccess: () => void;
  onSubmitError: () => void;
};

const normalizeTopics = (raw: string): string[] =>
  raw
    .split(/[,|\n]/g)
    .map((s) => s.trim())
    .filter(Boolean);

const buildCallbackRequestNotes = (
  consultationTitle: string,
  topics: string[],
) =>
  `Beratungsart:\n-${consultationTitle}\nThemen:\n${topics
    .map((t) => `-${t}`)
    .join('\n')}`;

export const useMultistepFormState = (
  args: UseMultistepFormStateArgs = {},
): UseMultistepFormStateResult => {
  const totalSteps = 3;
  const locale = args.locale ?? 'de';
  const presetConsultation = args.presetConsultation ?? null;
  const presetConsultationTitle = (presetConsultation?.title ?? '').trim();
  const hasPresetConsultation = Boolean(presetConsultation);
  const presetTopicsRaw = (args.presetTopics ?? '').trim();
  const presetTopics = useMemo(
    () => (presetTopicsRaw ? normalizeTopics(presetTopicsRaw) : []),
    [presetTopicsRaw],
  );
  const presetCallbackRequestNotes = useMemo(() => {
    if (!presetConsultationTitle || presetTopics.length === 0) return '';
    return buildCallbackRequestNotes(presetConsultationTitle, presetTopics);
  }, [presetConsultationTitle, presetTopics]);

  const presetWebsiteSelectedTopics = useMemo(() => {
    if (presetTopics.length === 0) return '';
    return presetTopics.join(', ');
  }, [presetTopics]);

  // submission
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isFinalStepSubmitting, setIsFinalStepSubmitting] = useState(false);
  const submitFinalStepRef = useRef<null | (() => void)>(null);
  const lockBackNavigation = presetTopics.length > 0;
  const initialConsultations = useMemo(
    () => (presetConsultation ? [presetConsultation] : []),
    [presetConsultation],
  );
  // domain
  const flow = useConsultationFlow(
    locale,
    initialConsultations,
    presetConsultationTitle,
  );

  // navigation
  const nav = useMultistepNavigation({
    totalSteps,
    isSuccess,
    isError,
    isFinalStepSubmitting,
    selectedConsultationTitle: flow.selectedConsultationTitle,
    selectedTopics: flow.selectedTopics,
    anliegen: flow.anliegen,
    onFinalSubmit: () => submitFinalStepRef.current?.(),
    lockBackNavigation,
    skipFirstStep: hasPresetConsultation,
  });
  const {
    setSelectedTopics: setFlowSelectedTopics,
    setSelectedConsultationTitle: setFlowSelectedConsultationTitle,
    resetDomain,
  } = flow;
  const { setCurrentStep, resetNavigation } = nav;

  const didApplyPresetsRef = useRef(false);

  useEffect(() => {
    if (didApplyPresetsRef.current) return;
    if (!hasPresetConsultation || presetTopics.length === 0) return;

    didApplyPresetsRef.current = true;

    setFlowSelectedTopics(presetTopics);
    setCurrentStep(3);
  }, [
    hasPresetConsultation,
    presetTopics,
    setFlowSelectedTopics,
    setCurrentStep,
  ]);

  const onToggleTopic = useCallback(
    (val: string) => {
      setFlowSelectedTopics((prev) => toggleStringInArray(prev, val));
    },
    [setFlowSelectedTopics],
  );

  const resetAll = useCallback(() => {
    didApplyPresetsRef.current = false;

    setIsSuccess(false);
    setIsError(false);
    setIsFinalStepSubmitting(false);
    resetNavigation();
    resetDomain();
    if (hasPresetConsultation && presetConsultationTitle) {
      setFlowSelectedConsultationTitle(presetConsultationTitle);
    }
  }, [
    resetNavigation,
    resetDomain,
    hasPresetConsultation,
    presetConsultationTitle,
    setFlowSelectedConsultationTitle,
  ]);

  const onSubmitStart = useCallback(() => {
    setIsError(false);
    setIsFinalStepSubmitting(true);
  }, []);
  const onSubmitSuccess = useCallback(() => {
    setIsFinalStepSubmitting(false);
    setIsError(false);
    setIsSuccess(true);
  }, []);
  const onSubmitError = useCallback(() => {
    setIsFinalStepSubmitting(false);
    setIsError(true);
  }, []);
  const registerFinalSubmit = useCallback((submit: () => void) => {
    submitFinalStepRef.current = submit;
  }, []);

  return {
    totalSteps,
    consultations: flow.consultations,
    loadingConsultations: flow.loadingConsultations,
    selectedConsultation: flow.selectedConsultation,
    topics: flow.topics,
    selectedConsultationTitle: flow.selectedConsultationTitle,
    selectedTopics: flow.selectedTopics,
    anliegen: flow.anliegen,
    currentStep: nav.currentStep,
    canGoBack: nav.canGoBack,
    canGoNext: nav.canGoNext,
    showFooter: nav.showFooter,
    stepIndicatorCurrent: nav.stepIndicatorCurrent,
    isSuccess,
    isError,
    isFinalStepSubmitting,
    presetCallbackRequestNotes,
    presetWebsiteSelectedTopics,
    registerFinalSubmit,
    setSelectedConsultationTitle: flow.setSelectedConsultationTitle,
    setSelectedTopics: flow.setSelectedTopics,
    setAnliegen: flow.setAnliegen,
    onToggleTopic,
    resetAll,
    onNext: nav.onNext,
    onBack: nav.onBack,
    onSubmitStart,
    onSubmitSuccess,
    onSubmitError,
  };
};

export default useMultistepFormState;
